import { parseSummaryDocument } from '@libertify/ui';
import type {
  ExecutiveSummary,
  MediaAsset,
  MediaProjectResponse,
} from '../../types';
import {
  assetUrl,
  projectUrl,
  readJson,
  resolvePresentAddons,
} from '../addOnAssets';
import type { KeyPointsContent, UseKeyPointsArgs } from './types';

/** One key for the summary read and the cache the save writes. */
export const keyPointsKey = (apiBaseUrl: string, projectId: string) =>
  ['key-points', apiBaseUrl, projectId] as const;

/**
 * The Key points read chain — the same three steps `useExperienceAddons`
 * runs for every add-on, for this one only:
 *
 *   project → storyboards[].add_ons[] → the Summary asset_id
 *   asset   → signed path
 *   path    → `ExecutiveSummary { content }`
 *
 * "Key points" is the frontend name for the backend `Summary` add-on.
 *
 * Strict where the viewer is lenient: a summary that can't be read rejects
 * instead of reading as empty, so an editor never offers an empty document
 * to be saved over the real one.
 *
 * The editor needs the sections the viewer deliberately ignores — the
 * generator marks them with headings or with standalone bold lines, and
 * `parseKeyPoints` only recognises headings so published experiences keep
 * rendering as they always have. Hence `parseSummaryDocument` here.
 */
export const loadKeyPoints = async ({
  apiBaseUrl,
  projectId,
  token,
}: UseKeyPointsArgs): Promise<KeyPointsContent> => {
  const project = await readJson<MediaProjectResponse>(
    projectUrl(apiBaseUrl, projectId),
    token,
  );
  const assetId = resolvePresentAddons(project).get('Summary')?.asset_id;
  if (!assetId) return { content: '', document: null };
  const asset = await readJson<MediaAsset>(
    assetUrl(apiBaseUrl, assetId),
    token,
  );
  const summary = await readJson<ExecutiveSummary>(asset.path);
  const content = summary.content ?? '';
  return {
    content,
    document: parseSummaryDocument(content),
    assetId,
    description: asset.description,
  };
};
